import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { authAPI, dashboardAPI } from './services/api';
import { LoginData, DashboardOverview, FinancesData, HealthData, FamilyData, GoalsData } from './types';
import Dashboard from './components/Dashboard';
import Finances from './components/Finances';
import Health from './components/Health';
import Family from './components/Family';
import Goals from './components/Goals';
import InvestmentsPortfolio from './components/InvestmentsPortfolio';
import LandingPage from './components/LandingPage';
import FAQ from './components/FAQ';
import News from './components/News';
import FAQLoggedIn from './components/FAQLoggedIn';
import NewsLoggedIn from './components/NewsLoggedIn';
import AIAssistant from './components/AIAssistant/AIAssistant';
import AdminPanel from './components/AdminPanel';
import { MobileBottomNav, MobileHeader } from './components/MobileNav';
import { useIsMobile } from './hooks/useMobile';
import './App.css';

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

function AppContent() {
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userRole, setUserRole] = useState<'user' | 'admin'>('user');
  const [dashboardData, setDashboardData] = useState<DashboardOverview | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Проверка токена при загрузке
  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    if (token) {
      setIsLoggedIn(true);
      if (userData) {
        const user = JSON.parse(userData);
        setUserRole(user.role || 'user');
      }
      loadDashboard();
    }
  }, []);

  const loadDashboard = async () => {
    try {
      const data = await dashboardAPI.getOverview();
      setDashboardData(data);
    } catch (err) {
      console.error('Failed to load dashboard:', err);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const loginData: LoginData = { email, password };
      const response = await authAPI.login(loginData);

      localStorage.setItem('token', response.token);
      localStorage.setItem('user', JSON.stringify(response.user));
      setIsLoggedIn(true);
      setUserRole(response.user.role || 'user');
      setShowAuthModal(false);
      await loadDashboard();
    } catch (err: any) {
      console.error('Auth error:', err);
      alert(err.response?.data?.message || 'Произошла ошибка при входе');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setIsLoggedIn(false);
    setUserRole('user');
    setDashboardData(null);
    navigate('/');
  };

  // Хук для определения мобильного устройства
  const isMobile = useIsMobile();

  if (isLoggedIn) {
    const isActive = (path: string) => location.pathname === path;
    
    // Мобильная версия
    if (isMobile) {
      return (
        <div className="app" style={{ 
          minHeight: '100vh', 
          background: '#f9fafb',
          paddingBottom: '70px' // Место для нижней навигации
        }}>
          <MobileHeader
            userName={dashboardData?.user?.name}
            onLogout={handleLogout}
            userRole={userRole}
            onAdminClick={() => navigate('/admin')}
          />

          {/* Main Content */}
          <main style={{ padding: '12px' }}>
            <Routes>
              <Route path="/" element={<Dashboard />} />
              <Route path="/finances" element={<Finances />} />
              <Route path="/health" element={<Health />} />
              <Route path="/family" element={<Family />} />
              <Route path="/goals" element={<Goals />} />
              <Route path="/investments" element={<InvestmentsPortfolio />} />
              <Route path="/faq" element={<FAQLoggedIn />} />
              <Route path="/news" element={<NewsLoggedIn />} />
              {userRole === 'admin' && <Route path="/admin" element={<AdminPanel />} />}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </main>

          <MobileBottomNav userRole={userRole} />
          <AIAssistant />
        </div>
      );
    }
    
    // Десктопная версия
    return (
      <div className="app" style={{ minHeight: '100vh', background: '#f9fafb' }}>
        {/* Header */}
        <header style={{ 
          background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)', 
          color: 'white', 
          padding: '15px 20px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <div style={{ maxWidth: '1400px', margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
              <div>
                <h1 style={{ margin: '0 0 5px 0', fontSize: '28px' }}>🚀 Avatar</h1>
                <p style={{ margin: 0, fontSize: '14px', opacity: 0.9 }}>
                  Добро пожаловать, {dashboardData?.user?.name || 'Пользователь'}!
                </p>
              </div>
              <button
                id="ai-assistant-header-button"
                style={{
                  padding: '12px 24px',
                  background: 'rgba(255,255,255,0.25)',
                  color: 'white',
                  border: '2px solid rgba(255,255,255,0.4)',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '16px',
                  transition: 'all 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.35)';
                  e.currentTarget.style.transform = 'scale(1.05)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.25)';
                  e.currentTarget.style.transform = 'scale(1)';
                }}
              >
                <span style={{ fontSize: '20px' }}>🤖</span>
                <span>AI Помощник</span>
              </button>
              {userRole === 'admin' && (
                <button
                  onClick={() => navigate('/admin')}
                  style={{
                    padding: '8px 16px',
                    background: 'rgba(220, 38, 38, 0.2)',
                    color: 'white',
                    border: '1px solid rgba(220, 38, 38, 0.4)',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'all 0.2s',
                    boxShadow: '0 4px 12px rgba(220, 38, 38, 0.2)'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.background = 'rgba(220, 38, 38, 0.35)';
                    e.currentTarget.style.transform = 'scale(1.05)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.background = 'rgba(220, 38, 38, 0.2)';
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                >
                  <span style={{ fontSize: '20px' }}>👑</span>
                  <span>Админка</span>
                </button>
              )}
            </div>
            <button
              onClick={handleLogout}
              style={{ 
                padding: '8px 20px', 
                background: 'rgba(255,255,255,0.2)', 
                color: 'white', 
                border: '1px solid rgba(255,255,255,0.3)', 
                borderRadius: '6px', 
                cursor: 'pointer',
                fontWeight: '500',
                transition: 'all 0.2s'
              }}
              onMouseOver={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.3)'}
              onMouseOut={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.2)'}
            >
              Выйти
            </button>
          </div>
        </header>

        {/* Navigation */}
        <nav style={{ 
          background: 'white', 
          boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
          position: 'sticky',
          top: 0,
          zIndex: 100
        }}>
          <div style={{ maxWidth: '1400px', margin: '0 auto', display: 'flex', gap: '5px', padding: '10px 20px' }}>
            <Link 
              to="/"
              style={{ 
                padding: '10px 20px', 
                textDecoration: 'none',
                color: isActive('/') ? '#3b82f6' : '#666',
                fontWeight: isActive('/') ? 'bold' : 'normal',
                borderBottom: isActive('/') ? '3px solid #3b82f6' : '3px solid transparent',
                transition: 'all 0.2s'
              }}
            >
              � Главная
            </Link>
            <Link 
              to="/finances" 
              style={{ 
                padding: '10px 20px', 
                textDecoration: 'none',
                color: isActive('/finances') ? '#16a34a' : '#666',
                fontWeight: isActive('/finances') ? 'bold' : 'normal',
                borderBottom: isActive('/finances') ? '3px solid #16a34a' : '3px solid transparent',
                transition: 'all 0.2s'
              }}
            >
              💰 Финансы
            </Link>
            <Link 
              to="/health"
              style={{ 
                padding: '10px 20px', 
                textDecoration: 'none',
                color: isActive('/health') ? '#3b82f6' : '#666',
                fontWeight: isActive('/health') ? 'bold' : 'normal',
                borderBottom: isActive('/health') ? '3px solid #3b82f6' : '3px solid transparent',
                transition: 'all 0.2s'
              }}
            >
              🏥 Здоровье
            </Link>
            <Link 
              to="/family" 
              style={{ 
                padding: '10px 20px', 
                textDecoration: 'none',
                color: isActive('/family') ? '#8b5cf6' : '#666',
                fontWeight: isActive('/family') ? 'bold' : 'normal',
                borderBottom: isActive('/family') ? '3px solid #8b5cf6' : '3px solid transparent',
                transition: 'all 0.2s'
              }}
            >
              👨‍👩‍👧‍👦 Семья
            </Link>
            <Link 
              to="/goals"
              style={{ 
                padding: '10px 20px', 
                textDecoration: 'none',
                color: isActive('/goals') ? '#f59e0b' : '#666',
                fontWeight: isActive('/goals') ? 'bold' : 'normal',
                borderBottom: isActive('/goals') ? '3px solid #f59e0b' : '3px solid transparent',
                transition: 'all 0.2s'
              }}
            >
              🎯 Цели
            </Link>
            <div style={{ marginLeft: 'auto', display: 'flex', gap: '5px' }}>
              <Link 
                to="/faq"
                style={{ 
                  padding: '10px 20px', 
                  textDecoration: 'none',
                  color: isActive('/faq') ? '#667eea' : '#666',
                  fontWeight: isActive('/faq') ? 'bold' : 'normal',
                  borderBottom: isActive('/faq') ? '3px solid #667eea' : '3px solid transparent',
                  transition: 'all 0.2s'
                }}
              >
                ❓ FAQ
              </Link>
              <Link 
                to="/news"
                style={{ 
                  padding: '10px 20px', 
                  textDecoration: 'none',
                  color: isActive('/news') ? '#667eea' : '#666',
                  fontWeight: isActive('/news') ? 'bold' : 'normal',
                  borderBottom: isActive('/news') ? '3px solid #667eea' : '3px solid transparent',
                  transition: 'all 0.2s'
                }}
              >
                📰 Новости
              </Link>
            </div>
          </div>
        </nav>

        {/* Main Content */}
        <main style={{ padding: '30px 20px', maxWidth: '1400px', margin: '0 auto' }}>
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/finances" element={<Finances />} />
            <Route path="/investments" element={<InvestmentsPortfolio />} />
            <Route path="/health" element={<Health />} />
            <Route path="/family" element={<Family />} />
            <Route path="/goals" element={<Goals />} />
            <Route path="/admin" element={<AdminPanel />} />
            <Route path="/faq" element={<FAQLoggedIn />} />
            <Route path="/news" element={<NewsLoggedIn />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>

        {/* Глобальный AI Ассистент */}
        <AIAssistant 
          currentPage={location.pathname}
        />

        {/* Кнопка админки (только для админов) */}
        {userRole === 'admin' && (
          <Link
            to="/admin"
            style={{
              position: 'fixed',
              bottom: '90px',
              right: '20px',
              width: '60px',
              height: '60px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #dc2626 0%, #991b1b 100%)',
              color: 'white',
              border: 'none',
              fontSize: '28px',
              cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(220, 38, 38, 0.4)',
              zIndex: 999,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              textDecoration: 'none',
              transition: 'transform 0.2s, box-shadow 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'scale(1.1)';
              e.currentTarget.style.boxShadow = '0 6px 20px rgba(220, 38, 38, 0.5)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(220, 38, 38, 0.4)';
            }}
            title="Админ-панель"
          >
            👑
          </Link>
        )}
      </div>
    );
  }

  // Show Landing Page or Public Pages
  if (!showAuthModal) {
    return (
      <Routes>
        <Route 
          path="/" 
          element={
            <LandingPage
              onLogin={() => setShowAuthModal(true)}
            />
          } 
        />
        <Route path="/faq" element={<FAQ />} />
        <Route path="/news" element={<News />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  }

  // Show Auth Modal
  return (
    <div 
      className="app" 
      style={{ 
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        zIndex: 1000
      }}
      onClick={() => setShowAuthModal(false)}
    >
      <div 
        style={{ 
          background: 'white', 
          padding: '2rem', 
          borderRadius: '12px', 
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)', 
          width: '100%', 
          maxWidth: '400px',
          position: 'relative'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={() => setShowAuthModal(false)}
          style={{
            position: 'absolute',
            top: '15px',
            right: '15px',
            background: 'none',
            border: 'none',
            fontSize: '24px',
            cursor: 'pointer',
            color: '#999'
          }}
        >
          ×
        </button>
        
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <h1 style={{ color: '#667eea', marginBottom: '0.5rem', fontSize: '28px' }}>🎯 AvatarMe</h1>
          <h2 style={{ margin: '0', fontSize: '1.5rem', fontWeight: 'normal' }}>
            Вход в аккаунт
          </h2>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
              Email:
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={{
                width: '100%',
                padding: '0.5rem',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                fontSize: '1rem',
                boxSizing: 'border-box'
              }}
              required
            />
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
              Пароль:
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={{
                width: '100%',
                padding: '0.5rem',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                fontSize: '1rem',
                boxSizing: 'border-box'
              }}
              required
            />
          </div>

          <button
            type="submit"
            style={{
              width: '100%',
              padding: '0.75rem',
              backgroundColor: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              fontSize: '1rem',
              fontWeight: '500',
              cursor: 'pointer',
              marginBottom: '1rem'
            }}
          >
            Войти
          </button>
        </form>

      </div>
    </div>
  );
}

export default App;
